import { type JSX, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { AppBackground } from "@/components/app-background";

/**
 * Stockpilot's OWN auth shell — deliberately NOT the shared `AuthLayout`
 * (split brand-panel + centered `AuthCard` glass surface) that made every clone
 * read as the same template, and deliberately NOT listto's editorial stacked
 * column either.
 *
 * Stockpilot is a warehouse / WMS operations product, so its auth is framed as
 * an **operations console / dock terminal**, not a marketing card:
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ ◳ Stockpilot          ● WAREHOUSE · ONLINE    │ ← utility rail (logo + status)
 *   ├──────────────────────────────────────────────┤
 *   │ ACCESS · SESSION                              │ ← eyebrow as a dock/manifest tag
 *   │ Sign in to continue.                          │ ← h1
 *   │ Supporting operational line.                  │
 *   │                                               │
 *   │ ▏ email   [____________________]              │ ← fields on a left "rail",
 *   │ ▏ pass    [____________________]              │    flat list, no glass card
 *   │   [ Sign in ]                                 │
 *   └──────────────────────────────────────────────┘
 *
 * Key structural differences from the shared kit + every sibling clone:
 * - No `GlassSurface` / `AuthCard` frame; the form is a flat field stack sitting
 *   directly on the ambient background, bounded only by a left accent rail and a
 *   top hairline — like a picking list / manifest, not a centered SaaS card.
 * - A persistent top **utility rail** (logo + live "warehouse status" chip) reads
 *   like an operations terminal header, not a brand hero.
 * - The eyebrow is a uppercased monospace **dock tag** (`ACCESS · SESSION`), not a
 *   rounded pill, reinforcing the logistics voice.
 * - Single left-aligned column (`max-w-md`), never a two-column split.
 *
 * The shell stays agnostic about the *form*: screens pour their fields in as
 * `children`, so it composes with disabled-until-ready (#2), SocialAuthButtons
 * (#10) and dev pre-fill (#12) untouched.
 *
 * ── SCROLL CONTRACT (#13 kit, adapted to stockpilot's shell) ──────────────
 * `AppBackground` is `position: fixed; overflow: hidden` and its content slot is
 * `h-full`, so it CLIPS its children. Here it is rendered as a pure backdrop
 * sibling and the content lives in its OWN scroll container.
 *
 * NOTE — stockpilot pins `html, body { overflow: hidden; height: 100% }` in
 * global.css (to suppress stray body scrollbars from portals), so the document
 * itself never scrolls. The kit therefore says: cap the auth container to the
 * viewport (`h-[100dvh] overflow-y-auto`, NOT `min-h-[100dvh]`) so that THIS
 * container — not the (locked) body — is the element that scrolls. With
 * `min-h-[100dvh]` the container would instead grow past the viewport and get
 * clipped by the locked body, leaving a tall form (register) or a short
 * landscape viewport unreachable. The inner column is `min-h-full` so it centres
 * when content is short and grows (overflowing the capped container) when tall.
 */

interface AuthConsoleProps {
  /** Status chip text in the utility rail (e.g. "Access" / "Recovery"). */
  status?: string;
  /** Dock/manifest tag above the headline (e.g. "ACCESS · SESSION"). */
  tag?: string;
  /** Page headline — the operational promise for this screen. */
  headline?: string;
  /** Supporting line under the headline. */
  tagline?: string;
  /**
   * Heading level for the headline. The console headline IS the page h1 for
   * stockpilot's auth (there is no card title). Pass "h2" only if the screen
   * owns its own h1 elsewhere.
   */
  headingLevel?: "h1" | "h2";
  /** The form (fields + actions), rendered flat on the ambient background. */
  children: JSX.Element;
  /** Footer slot (links: register / back home), rendered under the form. */
  footer?: JSX.Element;
}

export function AuthConsole(props: AuthConsoleProps) {
  const Heading = (headingProps: { children: JSX.Element }) =>
    (props.headingLevel ?? "h1") === "h2" ? (
      <h2 class="text-pretty text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
        {headingProps.children}
      </h2>
    ) : (
      <h1 class="text-pretty text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
        {headingProps.children}
      </h1>
    );

  return (
    <>
      <AppBackground />

      <div
        class="relative z-10 h-[100dvh] w-full overflow-x-hidden overflow-y-auto px-5 sm:px-6"
        style={{
          "padding-top": "max(1.5rem, env(safe-area-inset-top))",
          "padding-bottom": "max(1.5rem, env(safe-area-inset-bottom))",
          "padding-left": "max(1.25rem, env(safe-area-inset-left))",
          "padding-right": "max(1.25rem, env(safe-area-inset-right))",
        }}
      >
        <div class="mx-auto flex min-h-full w-full max-w-md flex-col py-6">
          {/* Utility rail — logo chip + live warehouse status. Operations header,
              not a brand hero. */}
          <header class="flex items-center justify-between gap-3 border-b border-border/70 pb-4">
            <Link
              to="/"
              class="group flex items-center gap-2.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              aria-label="Stockpilot"
            >
              <span class="grid h-9 w-9 place-items-center rounded-[28%] bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5 transition-transform group-hover:scale-105 group-active:scale-95">
                <svg class="h-[62%] w-[62%]" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                  <path
                    d="M9 12 16 8.5l7 3.5v8L16 23.5 9 20v-8Z"
                    stroke="currentColor"
                    stroke-width="1.9"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M9 12l7 3.5 7-3.5M16 15.5v8"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linejoin="round"
                    opacity="0.55"
                  />
                  <path
                    d="m12.6 15 2.4 2.4 4.4-4.4"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span class="text-base font-bold tracking-tight text-foreground">
                Stock<span class="text-accent">pilot</span>
              </span>
            </Link>
            <Show when={props.status}>
              <span class="inline-flex items-center gap-1.5 rounded-md border border-border bg-card/50 px-2.5 py-1 font-mono text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground">
                <span class="h-1.5 w-1.5 rounded-full bg-success" aria-hidden="true" />
                {props.status}
              </span>
            </Show>
          </header>

          <div class="flex min-h-0 flex-1 flex-col justify-center py-8">
            {/* Manifest header — dock tag + headline + supporting line. */}
            <Show when={props.headline}>
              <div class="space-y-3">
                <Show when={props.tag}>
                  <p class="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-accent">
                    {props.tag}
                  </p>
                </Show>
                <Heading>{props.headline}</Heading>
                <Show when={props.tagline}>
                  <p class="text-pretty text-base leading-relaxed text-muted-foreground">
                    {props.tagline}
                  </p>
                </Show>
              </div>
            </Show>

            {/* Form on a left accent rail — a picking/manifest list, not a card. */}
            <div
              class="mt-7 border-l-2 border-accent/50 pl-5"
              classList={{ "mt-0": !props.headline }}
            >
              {props.children}
            </div>

            <Show when={props.footer}>
              <footer class="mt-7 space-y-3 pl-5 text-sm">{props.footer}</footer>
            </Show>
          </div>
        </div>
      </div>
    </>
  );
}
